import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserRole } from '../../hooks/useUserRole';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  redirectPath?: string;
}

/**
 * A component that protects routes based on authentication and admin status.
 * 
 * @param children The route content to render if access is allowed
 * @param requireAdmin Whether the route requires admin privileges
 * @param redirectPath Where to redirect if access is denied (defaults to '/flight-ranges' or '/')
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  redirectPath
}) => {
  const { isAdmin, isAuthenticated, isLoading } = useUserRole();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div>Loading...</div>
          <div className="loading-subtitle">Checking permissions...</div>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to home
  if (!isAuthenticated) {
    // Only redirect if we're not already on the home page
    // This prevents redirect loops
    if (location.pathname !== '/') {
      return <Navigate to="/" state={{ from: location }} replace />;
    }
    return null; // Return null if we're already on the home page
  }

  // If admin required but user is not admin, redirect to specified path or flight ranges
  if (requireAdmin && !isAdmin) {
    return <Navigate to={redirectPath ?? "/flight-ranges"} state={{ from: location }} replace />;
  }

  // If all checks pass, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
